import * as React from 'react'
import { isNil, cloneDeep } from 'lodash'
import { Form, Input, Spin } from 'antd'
import { showError, ValidationError } from 'helpers/errors'
import {
  createDropdownRender,
  createSearchSuppliers,
  createLabelFactory,
  createFieldFactory,
  createHandleSubmit,
  isReadOnly,
} from 'helpers/formViews'
import { stopEvent } from 'helpers/events'
import { t } from 'helpers/i18n'
import Modal from 'elements/Modal'
import Select, { Option, getOptionProps } from 'elements/Select'
import SelectProducts from 'containers/Products/Select'
import InputNumber from 'elements/InputNumber'
import FormButtons from 'elements/FormButtons'

class FormView extends React.Component {
  state = {}

  constructor(props) {
    super(props)

    this.handleSubmit = createHandleSubmit(this)
    this.searchSuppliers = createSearchSuppliers(this)
  }

  componentDidMount() {
    this.fetchItem()
  }

  getItemId = () => [this.state.item?.id, this.props.linkTargetRecord?.supplierDetailId].find(Boolean)

  fetchItem = async ({ itemId = this.getItemId() } = {}) => {
    if (isNil(itemId) && !this.props.linkTargetRecord) {
      this.setState({ selectProductVisible: true })
    } else {
      try {
        const responses = await Promise.all([
          this.props.getItem(itemId).catch((error) => {
            this.props.onCancel?.()
            throw error
          }),
          this.props.getSettings({ type: this.props.settingsType ?? 'supplierDetails' }),
        ])

        const item = cloneDeep(responses[0].value.data)

        const suppliers = await this.props.getSuppliers(
          item.supplierId ? { supplierIds: [item.supplierId] } : undefined
        )

        this.setState({
          item,
          fieldSettings: responses[1].value.data.fieldSettings,
          suppliers: suppliers?.value?.data ?? {},
        })
      } catch (error) {
        showError({ error })
      }
    }
  }

  handleSelectProductOk = async () => {
    const { selectedProduct } = this.state

    if (selectedProduct) {
      const { productId, description: itemDescription, barcode } = selectedProduct

      this.setState({ selectProductVisible: false })

      try {
        const responses = await Promise.all([
          this.props.newItem({
            productId,
            itemDescription,
            barcode,
            price: 0,
            description: '',
          }),
          this.props.getSettings({ type: this.props.settingsType ?? 'supplierDetails' }),
          this.props.getSuppliers(),
        ])

        this.setState({
          item: responses[0].value.data,
          fieldSettings: responses[1].value.data.fieldSettings,
          suppliers: responses[2].value.data ?? {},
        })
      } catch (error) {
        showError({ error })
      }
    }
  }

  saveItem = async (item) => {
    if (!item) {
      throw new Error('item is undefined')
    }

    const saved = item.id ? await this.props.updateItems([item]) : await this.props.createItem([item])

    return saved
  }

  render() {
    const { item, fieldSettings, selectedProduct } = this.state

    if (!item || !fieldSettings) {
      return (
        <>
          <Spin />
          <Modal
            title={t('selectProduct')}
            visible={this.state.selectProductVisible}
            okText={t('select')}
            onOk={this.handleSelectProductOk}
            okButtonProps={{ disabled: !selectedProduct }}
            onCancel={this.props.onCancel}
            width={992}
          >
            <SelectProducts
              onSelect={(values) => this.setState({ selectedProduct: values[0] })}
              filterProps={{ privateDatabaseOnly: true }}
            />
          </Modal>
        </>
      )
    }

    const createLabel = createLabelFactory(fieldSettings)
    const createFieldDecorator = createFieldFactory(this.props.form, item, fieldSettings)
    const readOnly = isReadOnly(this)

    return (
      <Form layout="vertical" colon={false} onSubmit={readOnly ? stopEvent : this.handleSubmit}>
        <div className="form-items-container">
          <ValidationError errors={this.state.validationErrors} />
          <Form.Item label={createLabel('barcode')}>
            {createFieldDecorator('barcode')(<Input disabled />)}
          </Form.Item>
          <Form.Item label={createLabel('itemDescription')}>
            {createFieldDecorator('itemDescription')(<Input.TextArea rows={5} disabled />)}
          </Form.Item>
          <Form.Item label={createLabel('supplierId')}>
            {createFieldDecorator('supplierId')(
              <Select
                dropdownRender={createDropdownRender(this.state.suppliers)}
                onSearch={this.searchSuppliers}
                readOnly={readOnly}
                showSearch
              >
                {(this.state.suppliers?.items ?? [])
                  .filter((each) => each.id)
                  .map((each) => (
                    <Option key={each.id} value={each.id}>
                      <span {...getOptionProps(each)}>{each.name}</span>
                    </Option>
                  ))}
              </Select>
            )}
          </Form.Item>
          <Form.Item label={createLabel('supplierItemNumber')}>
            {createFieldDecorator('supplierItemNumber')(<Input />)}
          </Form.Item>
          <Form.Item label={createLabel('price')}>
            {createFieldDecorator('price')(<InputNumber min={0} currency />)}
          </Form.Item>
          <Form.Item label={createLabel('description')}>
            {createFieldDecorator('description')(<Input.TextArea rows={5} />)}
          </Form.Item>
        </div>
        <div className="form-buttons-container">
          <FormButtons
            readOnly={readOnly}
            onCancel={this.props.onCancel}
            onSubmit={this.handleSubmit}
            saveButtonLoading={this.state.saveButtonLoading}
            saveAndCloseButtonLoading={this.state.saveAndCloseButtonLoading}
          />
        </div>
      </Form>
    )
  }
}

export default Form.create()(FormView)
